'use client'
import { useState } from 'react'
import { Image, X, FlaskConical } from 'lucide-react'
import type { LabFile } from '@/types/file'
import { useClinicFormat } from '@context/ClinicTimezoneContext'

interface PatientMediaCardProps {
  file: LabFile | null
}

export function PatientMediaCard({ file }: PatientMediaCardProps) {
  const { formatDate } = useClinicFormat()
  const [previewOpen, setPreviewOpen] = useState(false)

  if (!file) {
    return (
      <section className="bg-ml-surface rounded-xl border border-ml-outline-variant/10 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-ml-outline-variant/10">
          <Image className="h-4 w-4 text-ml-primary" />
          <h3 className="text-sm font-bold text-ml-primary uppercase tracking-wider">
            Última Imagen
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 px-5 py-8">
          <FlaskConical className="h-8 w-8 text-ml-outline" />
          <p className="text-sm text-ml-outline italic text-center">
            Sin imágenes de laboratorio disponibles.
          </p>
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="bg-ml-surface rounded-xl border border-ml-outline-variant/10 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-ml-outline-variant/10">
          <Image className="h-4 w-4 text-ml-primary" />
          <h3 className="text-sm font-bold text-ml-primary uppercase tracking-wider">
            Última Imagen
          </h3>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs font-medium text-ml-on-surface mb-2 leading-tight">
            {file.original_name}
          </p>
          <p className="text-[11px] text-ml-on-surface-variant/60 mb-3">
            {formatDate(file.uploaded_at)} · {file.file_size_human}
          </p>

          <button
            onClick={() => setPreviewOpen(true)}
            className="w-full h-40 rounded-lg overflow-hidden border border-ml-outline-variant/10 bg-ml-surface-container-low flex items-center justify-center hover:opacity-90 transition-opacity"
          >
            <img
              src={file.download_url}
              alt={file.original_name}
              className="object-contain w-full h-full max-h-40"
              onError={(e) => {
                ;(e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </button>
        </div>
      </section>

      {/* Full-screen preview */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-black/60">
            <p className="text-sm text-white font-medium truncate max-w-[60%]">
              {file.original_name}
            </p>
            <button
              onClick={() => setPreviewOpen(false)}
              className="p-1 text-white hover:text-ml-primary transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-auto p-4">
            <img
              src={file.download_url}
              alt={file.original_name}
              className="max-w-full max-h-full object-contain rounded"
              onError={() => setPreviewOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  )
}
